import { useState } from "react";
import { toast } from "sonner";
import { Warning, Trash, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const CONFIRM_PHRASE = "BORRAR TODO";
const HEADER_TOKEN = "ERASE-ALL-OPERATIONAL-DATA";

/**
 * Zona de Peligro: borrar TODOS los datos operativos del gabinete.
 * Preserva datos maestros (aseguradoras, peritos, reparadores, baremos, etc.)
 * Doble confirmación: el admin debe escribir "BORRAR TODO" exactamente.
 */
export default function DangerZone() {
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const fetchStats = async () => {
    try {
      const r = await api.get("/admin/maintenance/db-stats");
      setStats(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error obteniendo stats");
    }
  };

  const openModal = async () => {
    setOpen(true);
    setPhrase("");
    setResult(null);
    await fetchStats();
  };

  const submit = async () => {
    if (phrase !== CONFIRM_PHRASE) {
      toast.error(`Debes escribir exactamente "${CONFIRM_PHRASE}"`);
      return;
    }
    setLoading(true);
    try {
      const r = await api.post(
        "/admin/maintenance/wipe-operational-data",
        {},
        { headers: { "X-Confirm-Wipe": HEADER_TOKEN } },
      );
      setResult(r.data);
      toast.success(
        `Borrado completado: ${r.data.total_documents} documentos y ${r.data.total_files} archivos eliminados.`,
      );
      await fetchStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error ejecutando el borrado");
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setOpen(false);
    setPhrase("");
    setResult(null);
  };

  return (
    <section
      className="border-2 border-red-400/60 bg-red-50/50 rounded-md p-5"
      data-testid="danger-zone"
    >
      <div className="flex items-start gap-3">
        <Warning className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" weight="fill" />
        <div className="flex-1">
          <h2 className="text-base font-semibold text-red-900 mb-1">Zona de peligro</h2>
          <p className="text-sm text-red-800/90 mb-3">
            Borrar todos los <b>datos operativos</b> del gabinete:
            expedientes, adjuntos, eventos de calendario, chats por expediente,
            landings públicas, facturas emitidas, videoperitajes, recordatorios
            enviados, gastos y rendimiento calculado, logs de actividad y
            tokens de recuperación. <b>Es irreversible.</b>
          </p>
          <p className="text-xs text-red-800/80 mb-4">
            ✅ Se <b>preservan</b>: usuarios, peritos, aseguradoras, reparadores,
            baremos, repositorio, gabinetes, clientes de facturación, configuración,
            horarios laborables.
          </p>
          <button
            onClick={openModal}
            data-testid="danger-zone-open"
            className="bg-red-600 hover:bg-red-700 text-white text-sm px-4 py-2 rounded-md font-semibold inline-flex items-center gap-2 transition-colors"
          >
            <Trash className="w-4 h-4" weight="bold" />
            Borrar datos operativos
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="wipe-modal">
          <div className="bg-white rounded-lg shadow-2xl max-w-xl w-full p-6">
            {!result ? (
              <>
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-red-700">
                    <Warning className="w-5 h-5" weight="fill" /> Confirmar borrado
                  </h3>
                  <button onClick={close} className="text-muted hover:text-foreground" data-testid="wipe-close">
                    <X className="w-5 h-5" weight="bold" />
                  </button>
                </div>

                {stats && (
                  <div className="bg-stone-50 rounded-md p-3 mb-4 text-xs font-mono max-h-48 overflow-y-auto border border-stone-200">
                    <div className="font-semibold text-stone-700 mb-1 text-[11px] uppercase tracking-wider">Estado actual de la BD</div>
                    {Object.entries(stats).map(([col, count]) => (
                      <div key={col} className="flex justify-between py-0.5">
                        <span className="text-stone-600">{col}</span>
                        <span className="font-semibold text-stone-900">{count}</span>
                      </div>
                    ))}
                  </div>
                )}

                <p className="text-sm mb-3">
                  Para confirmar, escribe exactamente:
                  <code className="ml-2 bg-stone-100 px-2 py-0.5 rounded text-red-700 font-bold">{CONFIRM_PHRASE}</code>
                </p>
                <input
                  type="text"
                  value={phrase}
                  onChange={(e) => setPhrase(e.target.value)}
                  placeholder={CONFIRM_PHRASE}
                  data-testid="wipe-confirm-input"
                  autoFocus
                  className="w-full px-3 py-2 border-2 border-red-300 rounded-md text-sm font-mono mb-4 focus:outline-none focus:border-red-500"
                />

                <div className="flex justify-end gap-2">
                  <button
                    onClick={close}
                    disabled={loading}
                    data-testid="wipe-cancel"
                    className="text-sm px-4 py-2 rounded-md hover:bg-stone-100 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={submit}
                    disabled={loading || phrase !== CONFIRM_PHRASE}
                    data-testid="wipe-confirm"
                    className="bg-red-600 hover:bg-red-700 text-white text-sm px-4 py-2 rounded-md font-semibold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2 transition-colors"
                  >
                    <Trash className="w-4 h-4" weight="bold" />
                    {loading ? "Borrando…" : "Borrar todo"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-green-700 mb-3">✅ Borrado completado</h3>
                <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4 text-sm">
                  <div><b>Documentos eliminados:</b> {result.total_documents}</div>
                  <div><b>Archivos físicos eliminados:</b> {result.total_files}</div>
                  <div><b>Ejecutado por:</b> {result.executed_by}</div>
                </div>
                <details className="text-xs font-mono mb-4">
                  <summary className="cursor-pointer text-stone-600 hover:text-stone-900">Ver detalle por colección</summary>
                  <div className="mt-2 bg-stone-50 rounded p-2 max-h-48 overflow-y-auto">
                    {Object.entries(result.deleted || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between"><span>{k}</span><span className="font-semibold">{v}</span></div>
                    ))}
                  </div>
                </details>
                <div className="flex justify-end">
                  <button
                    onClick={close}
                    data-testid="wipe-done"
                    className="bg-foreground text-white text-sm px-4 py-2 rounded-md font-medium"
                  >
                    Cerrar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
